import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import workflowService from './workflowService';
import CalendarioFechamento from './CalendarioFechamento';
import CapaContaContabil from './CapaContaContabil';
import CadastroCargosPage from './CadastroCargosPage';
import CadastroGruposPage from './CadastroGruposPage';
import CadastroUsuariosPage from './CadastroUsuariosPage';
import ConfigAlcadasPage from './ConfigAlcadasPage';
import GruposEconomicosPage from './GruposEconomicosPage';
import MatrizAcessosPage from './MatrizAcessosPage';
import DashboardIntegridadePage from './DashboardIntegridadePage';
import CadastroDeParaPage from './CadastroDeParaPage';
import CadastroJustificativasPage from './CadastroJustificativasPage';
import MatrizResponsabilidadesPage from './MatrizResponsabilidadesPage';
import MonitoramentoPage from './MonitoramentoPage';
import OnboardingWizardPage from './OnboardingWizardPage';
import {
  AuditoriaSoxPage,
  ConciliacoesPage,
  ConfiguracoesPage,
  DashboardPage,
  DetalhePage,
  GovernancaPage,
  LancamentosManuaisPage,
  PendenciasPage,
  PendenciasTransversaisPage,
} from './WorkflowModule';
import './workflow.scss';

const Spinner: React.FC<{ texto?: string }> = ({ texto = 'Carregando...' }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 12, color: '#a1a5b7' }}>
    <div style={{ width: 28, height: 28, border: '3px solid #e8f4fb', borderTopColor: '#005e96', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <span>{texto}</span>
  </div>
);

// Tela mostrada a quem NÃO é Administrador TI quando o Go-Live ainda não foi ativado — não dá
// pra deixar a pessoa cair no wizard de configuração (ela não tem permissão pra mexer nele), mas
// também não faz sentido bloquear com o popup de "recurso não incluído no plano": o módulo já
// está disponível, só falta alguém com a permissão certa configurar.
const AguardandoConfiguracao: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: 24 }}>
    <div style={{ maxWidth: 460, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🔧</div>
      <h2 style={{ margin: '0 0 10px', fontSize: 18, color: '#181c32' }}>Workflow SOX ainda não foi configurado</h2>
      <p style={{ margin: 0, fontSize: 14, color: '#5e6278', lineHeight: 1.6 }}>
        A configuração inicial deste módulo é feita apenas pelo <strong>Administrador TI</strong>.
        Peça para um administrador do sistema habilitar essa permissão para você (ou para o
        responsável técnico) em <strong>Cad. Usuários</strong> — depois disso o assistente de
        configuração fica disponível.
      </p>
    </div>
  </div>
);

const WorkflowRoutes: React.FC = () => {
  const { path } = useRouteMatch();
  const location = useLocation();
  const { usuario } = useAuth();

  const tenantId = usuario?.clienteID ?? 0;

  // ── Status de onboarding + permissão (buscados quando o tenantId estiver disponível) ──
  const [wfCarregando, setWfCarregando] = useState(true);
  const [onboardingResult, setOnboardingResult] = useState<any>(null);
  const [meuAdminTi, setMeuAdminTi] = useState(false);

  useEffect(() => {
    if (tenantId <= 0) {
      setOnboardingResult({ goLiveAtivo: true });
      setWfCarregando(false);
      return;
    }
    setWfCarregando(true);
    Promise.all([
      workflowService.onboardingStatus(tenantId),
      workflowService.getMeuPerfil().catch(() => null),
    ])
      .then(([s, perfil]) => {
        setOnboardingResult(s);
        setMeuAdminTi(!!perfil?.adminTi);
      })
      .catch(() => setOnboardingResult({ goLiveAtivo: true }))
      .finally(() => setWfCarregando(false));
  }, [tenantId]);

  if (wfCarregando || onboardingResult === null) return <Spinner texto="Verificando configuração..." />;

  // ── Guarda de onboarding — só atua no dashboard raiz ──────────────────────
  // Páginas de configuração (cadastro-cargos, config-alcadas, etc.) são sempre
  // acessíveis: o wizard precisa navegar para elas durante o onboarding.
  const isRootDashboard = location.pathname === path || location.pathname === `${path}/`;

  // Quando o wizard acaba de completar o Go-Live, passa este flag para evitar
  // que o status em cache (ainda com goLiveAtivo: false) redirecione de volta.
  const goLiveJustCompleted = (location.state as any)?.goLiveJustCompleted === true;

  if (!onboardingResult.goLiveAtivo && isRootDashboard && !goLiveJustCompleted) {
    // Só o Administrador TI pode configurar o onboarding — outros usuários não têm o que
    // fazer no wizard e não devem cair nele. Mostra um aviso explicando quem precisa agir,
    // em vez de bloquear com o popup de "recurso não incluído no plano" (o módulo já está
    // disponível, só falta alguém com a permissão certa configurar).
    if (!meuAdminTi) return <AguardandoConfiguracao />;

    return (
      <Redirect
        to={{
          pathname: `${path}/onboarding`,
          // Passa o status já buscado para o wizard não precisar buscar de novo
          state: { prefetchedStatus: onboardingResult },
        }}
      />
    );
  }

  // ── Rotas ──────────────────────────────────────────────────────────────────
  return (
    <Switch>
      <Route exact path={path} component={DashboardPage} />
      <Route exact path={`${path}/pendencias`} component={PendenciasPage} />
      <Route exact path={`${path}/conciliacoes`} component={ConciliacoesPage} />
      <Route exact path={`${path}/conciliacoes/:id`} component={DetalhePage} />
      <Route exact path={`${path}/conciliacoes/:id/capa`} component={CapaContaContabil} />
      <Route exact path={`${path}/calendario-fechamento`} component={CalendarioFechamento} />
      <Route exact path={`${path}/lancamentos-manuais`} component={LancamentosManuaisPage} />
      <Route exact path={`${path}/pendencias-transversais`} component={PendenciasTransversaisPage} />
      <Route exact path={`${path}/auditoria-sox`} component={AuditoriaSoxPage} />
      <Route exact path={`${path}/governanca-ti`} component={GovernancaPage} />
      <Route exact path={`${path}/configuracoes`} component={ConfiguracoesPage} />
      <Route exact path={`${path}/cadastro-usuarios`} component={CadastroUsuariosPage} />
      <Route exact path={`${path}/cadastro-cargos`} component={CadastroCargosPage} />
      <Route exact path={`${path}/cadastro-grupos`} component={CadastroGruposPage} />
      <Route exact path={`${path}/grupos-economicos`} component={GruposEconomicosPage} />
      <Route exact path={`${path}/matriz-acessos`} component={MatrizAcessosPage} />
      <Route exact path={`${path}/monitoramento`} component={MonitoramentoPage} />
      <Route exact path={`${path}/config-alcadas`} component={ConfigAlcadasPage} />
      <Route exact path={`${path}/onboarding`} component={OnboardingWizardPage} />
      <Route exact path={`${path}/cadastro-depara`} component={CadastroDeParaPage} />
      <Route exact path={`${path}/cadastro-justificativas`} component={CadastroJustificativasPage} />
      <Route exact path={`${path}/matriz-responsabilidades`} component={MatrizResponsabilidadesPage} />
      <Route exact path={`${path}/dashboard-integridade`} component={DashboardIntegridadePage} />
      <Redirect to={path} />
    </Switch>
  );
};

export default WorkflowRoutes;
